import {
  BlocklistsModel,
  CompanyType,
  CustomFieldsModel,
  CustomFieldType,
  IpadLang,
  Lang,
  PrimaryKeyType,
  VisitAuthorizationStatusType,
  VisitCheckType,
  VisitsModel,
} from '@w3lcome/types';
import { colors } from '_/config/theme';
import { getBase64String } from '_/helpers/getBase64String';
import getCountryByLang from '_/helpers/getCountryByLang';
import { optimizeCheckinPicture, optimizeSignature } from '_/helpers/optimizeCheckinImages';
import { CheckinInterface } from '_/interfaces/CheckinInterface';
import { CustomFieldWithError } from '_/interfaces/CustomFieldWithError';
import { VisitCheckinErrors } from '_/interfaces/VisitCheckinErrors';
import { VisitsModelCheckin } from '_/interfaces/VisitsModelCheckin';
import { ipadLogsApi, visitsApi } from '_/services/api';
import logger from '_/services/logger';
import bigInt from 'big-integer';
import { cpf } from 'cpf-cnpj-validator';
import { validate as validateEmail } from 'email-validator';
import parsePhoneNumberFromString, {
  AsYouType,
  CountryCode,
  isValidPhoneNumber,
  NationalNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import uuid from 'react-native-uuid';

import { useNetInfo } from '@react-native-community/netinfo';
import { StackNavigationProp } from '@react-navigation/stack';
import getVisitIdByQRCode from '_/helpers/getVisitIdByQRCode';
import { decodeQRCode } from '_/helpers/totp';
import { VALID_BRAZIL_DDDS } from '_/helpers/validBrazilDDD';
import { AppRoute, RootStackParamList } from '_/navigation/types';
import { Platform } from 'react-native';
import { countries } from '../helpers/countryDials';
import { useAuth } from './AuthContext';
import { useChildrenCompanies } from './ChildrenCompaniesContex';
import { useCustomization } from './CustomizationContext';
import { useCentralizedValuesFallback } from './useCentralizedValuesFallback';
import { useObjectState } from './useObjectState';

type GetVisitorByPrimaryKeyReturn =
  | {
      visit: Partial<VisitsModelCheckin> | undefined;
      visits?: VisitsModel[] | undefined;
    }
  | undefined;

interface VisitContextData {
  visit: Partial<VisitsModelCheckin>;
  loading: boolean;
  blockedListed: BlocklistsModel | undefined;
  setLoading: (loading: boolean) => void;
  setVisit: (values: Partial<VisitsModelCheckin>) => void;
  resetData: () => void;
  checkFields: (fields: CustomFieldWithError[], isHostForm?: boolean) => CustomFieldWithError[];
  getVisitorByPrimaryKey: () => Promise<GetVisitorByPrimaryKeyReturn>;
  getVisitDataByField: (field?: CustomFieldsModel) => string | boolean;
  getVisitDataByFeedback: (feedback?: CustomFieldsModel) => string;
  isValidVisitDataByField: (field?: CustomFieldsModel) => boolean | undefined;
  setVisitDataByField: (value: string, field?: CustomFieldsModel) => void;
  setVisitDataByFeedback: (value: string, feedback?: CustomFieldsModel) => void;
  checkinVisit: (visit: Partial<VisitsModelCheckin>) => Promise<VisitsModelCheckin>;
  setVisitParser: (visit: VisitsModel) => void;
  setIsPrintVisitBadge: (value: boolean) => void;
  isPrintVisitBadge: boolean;
  showMessageErrorUniqueVisit: boolean;
  canceledVisits: null | VisitsModel[];
  setVisitDataByHostFields: (
    value: string,
    hostField?: CustomFieldsModel,
    countryCode?: CountryCode
  ) => void;
  getVisitDataByHostFields: (hostField?: CustomFieldsModel) => string;
  getVisitByQRCode: (value: string) => Promise<VisitsModel>;
  navigateToHome: (navigation: StackNavigationProp<RootStackParamList>) => void;
  defaultCountryCode: CountryCode | undefined;
}

// Check-ins at or above this duration are reported to the ipad logs with their payload sizes.
const SLOW_CHECKIN_LOG_MS = 5000;

function getBase64SizeKB(base64?: string | null) {
  return base64 ? Math.round((base64.length * 3) / 4 / 1024) : 0;
}

// Size the text adds to the JSON body (as sent over the wire).
function getTextSizeKB(text?: string | null) {
  return text ? Math.round(text.length / 1024) : 0;
}

const VisitContext = createContext<VisitContextData>({} as VisitContextData);

type VisitType = {
  children: React.ReactNode;
};

export const VisitProvider: React.FC<VisitType> = ({ children }) => {
  const { ipad } = useAuth();
  const {
    selectedOrFallbackCustomization,
    selectedOrFallbackNDA,
    selectedOrFallbackCompany,
    selectedOrFallbackVisitFlowType,
    selectedOrFallbackVisitFlowTypes,
  } = useCentralizedValuesFallback();

  const { company } = useCustomization();

  const [blockedListed, setBlockedListed] = useState<BlocklistsModel>();
  const [visit, setVisit, resetVisit] = useObjectState<Partial<VisitsModelCheckin>>({});

  const [loading, setLoading] = useState(false);
  const { i18n, t } = useTranslation(
    `translation_${selectedOrFallbackCustomization?.translationId}`
  );
  const [isPrintVisitBadge, setIsPrintVisitBadge] = useState(true);
  const [showMessageErrorUniqueVisit, setShowMessageErrorUniqueVisit] = useState(false);
  const [canceledVisits, setCanceledVisits] = useState<null | VisitsModel[]>(null);

  const { selectedCompanyId, removeRemoteData } = useChildrenCompanies();

  const netInfo = useNetInfo();

  const defaultCountryCode: CountryCode | undefined = useMemo(() => {
    const country = countries.find(
      (country) => country.name.toLowerCase() === selectedOrFallbackCompany?.country?.toLowerCase()
    );

    return (country?.code as CountryCode) || getCountryByLang(i18n.language as IpadLang);
  }, [selectedOrFallbackCompany, i18n.language]);

  useEffect(() => {
    if (defaultCountryCode && !visit.countryCode) {
      setVisit({ countryCode: defaultCountryCode });
    }
  }, [defaultCountryCode]);

  function getVisitorPrimaryKey() {
    if (!selectedOrFallbackVisitFlowType?.primaryKey) {
      return null;
    }

    if (
      visit.isForeigner &&
      (selectedOrFallbackVisitFlowType.primaryKey === PrimaryKeyType.CPF ||
        selectedOrFallbackVisitFlowType.primaryKey === PrimaryKeyType.RG)
    ) {
      return visit.document;
    }

    switch (selectedOrFallbackVisitFlowType.primaryKey) {
      case PrimaryKeyType.CPF:
        return visit.cpf;
      case PrimaryKeyType.DOCUMENT:
        return visit.document;
      case PrimaryKeyType.EMAIL:
        return visit.email;
      case PrimaryKeyType.PHONE:
        return parsePhoneNumberWithError(visit.phone || '', visit.countryCode)?.number?.replace(
          '+',
          ''
        );
      case PrimaryKeyType.RG:
        return visit.rg;
    }
  }

  function setVisitParser(visit: VisitsModel) {
    let countryCode: CountryCode | undefined = defaultCountryCode;
    let phoneNumber: NationalNumber | undefined;

    if (visit.phone) {
      const { country, nationalNumber } = parsePhoneNumberWithError(`+${visit.phone}`);
      countryCode = country;
      phoneNumber = nationalNumber;
    }

    const visitFormatted = {
      ...visit,
      phone: (phoneNumber as string) ?? null,
      countryCode,
    };

    setVisit(visitFormatted);
    return visitFormatted;
  }

  const getVisitFn = (
    visitPrimaryKey: string,
    isVisitorForeigner: boolean,
    visitFlowTypeId: string
  ) => {
    if (selectedOrFallbackCustomization?.searchAllFlowTypes) {
      const visitFlowTypesIds = selectedOrFallbackVisitFlowTypes.map((vft) => vft.id);
      return visitsApi.getVisitByPrimaryKeyAllFlowType(
        visitPrimaryKey,
        visitFlowTypeId,
        visitFlowTypesIds,
        isVisitorForeigner
      );
    }

    if (selectedCompanyId) {
      return visitsApi.getChildrenVisitByPrimaryKey(
        visitPrimaryKey,
        selectedCompanyId,
        visitFlowTypeId,
        isVisitorForeigner
      );
    }

    return visitsApi.getVisitByPrimaryKey(visitPrimaryKey, visitFlowTypeId, isVisitorForeigner);
  };

  async function getVisitorByPrimaryKey() {
    const visitPrimaryKey = getVisitorPrimaryKey();
    const lowerCasedPrimaryKeyType = selectedOrFallbackVisitFlowType?.primaryKey.toLowerCase();

    const visitFlowTypeId = selectedOrFallbackVisitFlowType?.id;

    if (!visitPrimaryKey || !lowerCasedPrimaryKeyType || !visitFlowTypeId) {
      return;
    }

    const isVisitorForeigner = visit.isForeigner ?? false;

    try {
      setLoading(true);

      const {
        visit: visitFound,
        canceledVisits,
        visits,
      } = await getVisitFn(visitPrimaryKey, isVisitorForeigner, visitFlowTypeId);

      if (canceledVisits) {
        setCanceledVisits(canceledVisits);
      }

      if (visitFound) {
        const visitFormatted = setVisitParser(visitFound);
        setLoading(false);
        return { visit: visitFormatted };
      }

      setLoading(false);

      return { visit, visits };
    } catch (error: any) {
      logger(error);
      setLoading(false);

      handleError(error, visitPrimaryKey);
    }
  }

  const handleError = (error: any, visitPrimaryKey: string) => {
    const errorCode = error?.response?.data?.data?.errorCode as VisitCheckinErrors;

    const errorActions: Record<VisitCheckinErrors, () => void> = {
      [VisitCheckinErrors.blockedList]: () => {
        showMessage({
          message: t('unauthorizedEntry'),
          description: t(
            selectedOrFallbackCompany?.visitsConfig?.customBlocklistErrorMessage ||
              'checkinScreen.blockedList'
          ),
          backgroundColor: colors.warning,
        });
      },
      [VisitCheckinErrors.uniqueVisit]: () => {
        setShowMessageErrorUniqueVisit(true);
        setVisitParser(error?.response?.data?.data?.visit?.[0]);
        ipadLogsApi.insert({
          companyId: ipad?.companyId,
          ipadId: ipad?.id,
          type: 'Unique Visit Error',
          message: `VISITOR: ${visitPrimaryKey} - ERROR: ${t('checkinScreen.uniqueVisit')}`,
        });
      },
      [VisitCheckinErrors.allowList]: () => {
        showMessage({
          message: t('error'),
          description: t('checkinScreen.allowList'),
          backgroundColor: colors.danger,
        });
      },
    };

    if (errorActions[errorCode]) {
      errorActions[errorCode]();
      throw error;
    }
  };

  const formatValue = (field: CustomFieldsModel, value: string) => {
    const numberOfCharacters = selectedOrFallbackVisitFlowType?.numberOfCharacters || 0;

    if (
      field.type === CustomFieldType.IDENTITY_CARD &&
      numberOfCharacters > 0 &&
      value.length > numberOfCharacters
    ) {
      return value.substring(value.length - numberOfCharacters);
    }

    return value;
  };

  function getVisitDataByField(field?: CustomFieldsModel) {
    if (!field) {
      return '';
    }

    if (field.type === CustomFieldType.PHONE && visit?.phone) {
      if (visit?.phone.charAt(visit?.phone.length - 1) === ')') {
        return visit?.phone.replace(')', '');
      } else {
        const formattedNumber = new AsYouType(visit?.countryCode).input(visit.phone);
        return formattedNumber;
      }
    }

    if (field.type === CustomFieldType.IDENTITY_CARD) {
      return formatValue(field, visit.identity_card || '');
    }

    if (!field.customType) {
      const type = field.type.toLowerCase() as keyof VisitsModel;
      const data = visit[type] || '';

      if (field.type === CustomFieldType.CPF) {
        return cpf.format(data as string);
      }

      return data as string;
    }

    if (field.type === CustomFieldType.BOOLEAN) {
      const booleanField = visit.customFields?.find((cf) => cf.customFieldId === field.id);
      return booleanField?.value === 'true';
    }

    const value = visit.customFields?.find((cf) => cf.customFieldId === field.id)?.value || '';

    if (field.type === CustomFieldType.TEXT && field.validationExpression) {
      const regexParser = /^\/(.*)\/([gimyus]*)$/;
      const pattern = field.validationExpression.match(regexParser)?.[1] || '';
      const flags = field.validationExpression.match(regexParser)?.[2];
      const regex = new RegExp(pattern, flags);

      return value.replace(regex, '');
    }

    return value;
  }

  function setVisitDataByField(value: string, field?: CustomFieldsModel) {
    if (!field) {
      return;
    }

    if (!field.customType) {
      const type = field.type.toLowerCase() as keyof VisitsModel;
      setVisit({ [type]: formatValue(field, value) });
    } else {
      const currentCustomField = visit.customFields?.find((cf) => cf.customFieldId === field.id);
      if (currentCustomField) {
        setVisit({
          customFields: visit.customFields?.map((cf) =>
            cf.customFieldId === field.id ? { ...cf, value } : cf
          ),
        });
      } else {
        const customField: any = { customFieldId: field.id, value };
        setVisit({ customFields: (visit.customFields || []).concat([customField]) });
      }
    }
  }

  function setVisitDataByFeedback(value: string, feedback?: CustomFieldsModel) {
    if (!feedback) {
      return;
    }

    if (!feedback.customType) {
      const type = feedback.type.toLowerCase() as keyof VisitsModel;
      setVisit({ [type]: value });
    } else {
      const currentFeedback = visit.feedbacks?.find((cf) => cf.customFieldId === feedback.id);
      if (currentFeedback) {
        setVisit({
          feedbacks: visit.feedbacks?.map((cf) =>
            cf.customFieldId === feedback.id ? { ...cf, value } : cf
          ),
        });
      } else {
        const feedbacks: any = { customFieldId: feedback.id, value };
        setVisit({ feedbacks: (visit.feedbacks || []).concat([feedbacks]) });
      }
    }
  }

  function getVisitDataByFeedback(feedback?: CustomFieldsModel) {
    if (!feedback) {
      return '';
    }

    if (!feedback.customType) {
      const type = feedback.type.toLowerCase() as keyof VisitsModel;
      const data = visit[type] || '';
      return data as string;
    }

    return visit.feedbacks?.find((cf) => cf.customFieldId === feedback.id)?.value || '';
  }

  function setVisitDataByHostFields(value: string, hostField?: CustomFieldsModel) {
    if (!hostField) return;

    const currentHostField = visit.extraHostFormFields?.find(
      (cf) => cf.customFieldId === hostField.id
    );

    if (currentHostField) {
      setVisit({
        extraHostFormFields: visit.extraHostFormFields?.map((cf) =>
          cf.customFieldId === hostField.id ? { ...cf, value } : cf
        ),
      });
    } else {
      const extraHostFormFields: any = { customFieldId: hostField.id, value };
      setVisit({
        extraHostFormFields: (visit.extraHostFormFields || []).concat([extraHostFormFields]),
      });
    }
  }

  function getVisitDataByHostFields(hostField?: CustomFieldsModel) {
    if (!hostField) {
      return '';
    }

    return visit.extraHostFormFields?.find((cf) => cf.customFieldId === hostField.id)?.value || '';
  }

  function isNameValid(name: string) {
    if (name.length < 3) {
      return false;
    }

    if (/[^\u0000-\u00ff]/.test(name)) {
      return true;
    }

    const isFullName = new RegExp(
      `^[a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*( [a-zA-Zà-úÀ-Ú]([-']?[a-zA-Zà-úÀ-Ú]+)*)+$`
    );

    return selectedOrFallbackVisitFlowType?.fullNameRequired ? isFullName.test(name.trim()) : true;
  }

  function isValidVisitDataByField(field?: CustomFieldsModel): boolean {
    if (!field) return false;

    const { type, required, disabled } = field;
    const fieldIsNotRequired = !required || !!disabled;

    const data = getVisitDataByField(field);

    if (type === CustomFieldType.BOOLEAN) {
      return fieldIsNotRequired || data === true;
    }

    const dataStr = typeof data === 'string' ? data.trim() : String(data ?? '').trim();

    if (visit.isForeigner && (type === CustomFieldType.CPF || type === CustomFieldType.RG)) {
      if (!required) return true;
      const doc = getVisitDataByField({ ...field, type: CustomFieldType.DOCUMENT });
      return !!doc && isValidDocument(String(doc));
    }

    if (!dataStr) return fieldIsNotRequired;

    switch (type) {
      case CustomFieldType.CPF:
        return cpf.isValid(dataStr);

      case CustomFieldType.DOCUMENT:
        return isValidDocument(dataStr);

      case CustomFieldType.NAME:
        return isNameValid(dataStr);

      case CustomFieldType.EMAIL:
        return validateEmail(dataStr);

      case CustomFieldType.PHONE:
        if (visit.countryCode === 'BR' && dataStr.length > 8) {
          return isValidBrazilPhone(dataStr);
        }
        return isValidPhoneNumber(dataStr, visit.countryCode);

      default:
        return true;
    }
  }

  function isValidDocument(doc: string): boolean {
    const trimmed = doc.trim();

    if (trimmed.length < 4 || trimmed.length > 25) return false;

    return true;
  }

  function isValidDDD(ddd: string): boolean {
    return VALID_BRAZIL_DDDS.includes(ddd);
  }

  function isValidBrazilPhone(phone?: string): boolean {
    if (!phone) return false;

    const p = parsePhoneNumberFromString(phone, 'BR');
    if (!p?.isValid()) return false;

    const national = String(p.nationalNumber);

    // ❌ bloquear sequências de dígitos repetidos (11111111, 22222222 etc)
    if (/^(\d)\1+$/.test(national)) return false;

    const ddd = national.slice(0, 2);
    if (!isValidDDD(ddd)) return false;

    // 📏 Validação por comprimento:
    // - Fixo = 10 dígitos (2 DDD + 8 número)
    // - Celular = 11 dígitos (2 DDD + 9 número, obrigatoriamente começando com 9)
    if (national.length === 11) {
      // celular → primeiro dígito após DDD precisa ser 9
      return national[2] === '9';
    }

    if (national.length === 10) {
      // fixo → não pode começar com 9
      return national[2] !== '9';
    }

    return false;
  }

  function isValidExtraHostFormDataByField(field?: CustomFieldsModel) {
    if (!field) {
      return false;
    }

    const fieldIsNotRequired = !field.required || field.disabled;
    const data = getVisitDataByHostFields(field);

    if (field.type === CustomFieldType.BOOLEAN) {
      return fieldIsNotRequired || data === 'true';
    }

    if (!data) {
      return fieldIsNotRequired;
    }

    return !!data;
  }

  function checkFields(fields: CustomFieldWithError[], isHostForm = false): CustomFieldWithError[] {
    return fields.map((field) => {
      return {
        ...field,
        hasError: isHostForm
          ? !isValidExtraHostFormDataByField(field)
          : !isValidVisitDataByField(field),
      };
    });
  }

  function navigateToHome(navigation: StackNavigationProp<RootStackParamList>) {
    resetData();
    if (selectedOrFallbackCompany?.type !== CompanyType.INDEPENDENT) removeRemoteData();
    navigation.reset({
      index: 0,
      routes: [{ name: AppRoute.HOME }],
    });
  }

  function resetData() {
    resetVisit({
      id: uuid.v4().toString(),
      checkinType: VisitCheckType.IPAD,
      checkinByIpadId: ipad?.id,
      countryCode: defaultCountryCode,
    });
    setIsPrintVisitBadge(true);
    setShowMessageErrorUniqueVisit(false);
    setBlockedListed(undefined);
    setLoading(false);
    setCanceledVisits(null);
  }

  async function checkinVisit(visit: Partial<VisitsModelCheckin>) {
    const customFields = visit?.customFields?.filter((field) => field.customFieldId);

    const parsedVisit: Partial<VisitsModelCheckin> = {
      ...visit,
      customFields,
      finishCheckinAt: new Date(),
      checkinAt: new Date(),
      picture: null,
      // Only used on the totem for the Serpro validation; the server ignores it.
      coHostPicture: undefined,
      signedNda: undefined,
      lang: i18n.language as Lang,
      phone: visit.phone
        ? (parsePhoneNumberWithError(visit.phone, visit.countryCode).number as string)?.replace(
            '+',
            ''
          )
        : undefined,
      checkoutAt: undefined,
      ...(selectedOrFallbackCustomization?.authorizationEnabled &&
        !visit.meeting && { authorizationStatus: VisitAuthorizationStatusType.AWAITING }),
      checkinByIpadId: ipad?.id,
      ...(selectedOrFallbackVisitFlowType?.id && {
        visitFlowTypeId: selectedOrFallbackVisitFlowType?.id,
      }),
    };

    const originalPicture = getBase64String(visit.picture?.base64);
    const optimizeStartedAt = Date.now();
    const [visitNda, visitPicture] = await Promise.all([
      optimizeSignature(visit.signedNda),
      optimizeCheckinPicture(originalPicture),
    ]);
    const optimizeMs = Date.now() - optimizeStartedAt;

    const value: CheckinInterface = {
      visit: parsedVisit,
      visitNda,
      visitPicture,
      visitNdaText: selectedOrFallbackNDA?.ndaText,
    };

    const payloadSizes = `PICTURE_KB: ${getBase64SizeKB(originalPicture)} -> ${getBase64SizeKB(
      visitPicture
    )} - NDA_KB: ${getBase64SizeKB(visit.signedNda)} -> ${getBase64SizeKB(
      visitNda
    )} - NDA_TEXT_KB: ${getTextSizeKB(value.visitNdaText)} - OPTIMIZE_MS: ${optimizeMs}`;

    const requestStartedAt = Date.now();

    try {
      // if (!netInfo.isConnected) {
      //   VisitDB.create(value);
      //   return;
      // }

      const result =
        selectedCompanyId || company?.type === CompanyType.PARENT
          ? await visitsApi.childrenCheckin(value, {
              params: { companyId: selectedCompanyId ?? company?.id },
            })
          : await visitsApi.checkin(value);

      const requestMs = Date.now() - requestStartedAt;
      if (requestMs >= SLOW_CHECKIN_LOG_MS) {
        ipadLogsApi
          .insert({
            companyId: ipad?.companyId,
            ipadId: ipad?.id,
            type: 'Slow Checkin',
            message: `VISIT: ${parsedVisit.id} - REQUEST_MS: ${requestMs} - ${payloadSizes}`,
          })
          .catch(() => {});
      }

      return result;
    } catch (error: any) {
      logger(`[checkinVisit] error: ${error}`);

      ipadLogsApi.insert({
        companyId: ipad?.companyId,
        ipadId: ipad?.id,
        type: 'Checkin Error',
        message: `VISITOR: ${JSON.stringify({ ...parsedVisit, signedNda: null, picture: null })}
        -
        STATUS: ${error?.response?.status ?? 'NO_RESPONSE'}
        _
        ERROR: ${error?.response?.data?.message || error.message || 'Unknown error'}
        _
        CONNECTED: ${netInfo.isConnected}
        _
        REQUEST_MS: ${Date.now() - requestStartedAt} - ${payloadSizes}
        `,
      });

      if (!netInfo.isConnected) {
        showMessage({
          message: t('networkError'),
          description: t('checkYourConnection'),
          backgroundColor: colors.warning,
        });
      } else if (error?.response?.status >= 500) {
        showMessage({
          message: t('error'),
          description: t('serverErrorMessage'),
          backgroundColor: colors.danger,
        });
      } else if (Platform.OS !== 'web') {
        // VisitDB.create(value);
      }

      // if (error?.response?.status >= 500) {
      //   showMessage({
      //     message: t('error'),
      //     description: t('serverErrorMessage'),
      //     backgroundColor: colors.danger,
      //   });
      // } else if (Platform.OS !== 'web') {
      //   // VisitDB.create(value);
      // }
      throw error;
    }
  }

  async function getVisitByQRCode(data: string) {
    try {
      const visitId = getVisitIdByQRCode(data);

      if (visitId) {
        return selectedCompanyId || company?.type === CompanyType.PARENT
          ? await visitsApi.getChildrenVisit(visitId, {
              params: { companyId: selectedCompanyId || company?.id },
            })
          : await visitsApi.getItem(visitId);
      }
      const isNumber = new RegExp('^[0-9]*$');

      if (isNumber.test(data)) {
        const binaryValue = bigInt(data).toString(2);
        const cardNumber = binaryValue.length > 40 ? decodeQRCode(data) : data;
        const params = {
          companyId: selectedCompanyId || company?.id,
          cardNumber: cardNumber,
          '$sort[createdAt]': -1,
        };

        const result =
          selectedCompanyId || company?.type === CompanyType.PARENT
            ? await visitsApi.getChildrenVisits({ params })
            : await visitsApi.getList(params);

        return result.data[0];
      }
    } catch (error: any) {
      if (error.response?.data?.message === VisitCheckinErrors.blockedList) {
        return showMessage({
          message: t('unauthorizedEntry'),
          description: t('checkinScreen.blockedList'),
          backgroundColor: colors.warning,
        });
      }

      logger(`[getVisitByQRCode] error: ${error}`);
      throw error;
    }
  }

  return (
    <VisitContext.Provider
      value={{
        visit,
        loading,
        blockedListed,
        setLoading,
        setVisit,
        resetData,
        checkFields,
        getVisitorByPrimaryKey,
        isValidVisitDataByField,
        getVisitDataByField,
        setVisitDataByField,
        setVisitDataByFeedback,
        getVisitDataByFeedback,
        checkinVisit,
        setVisitParser,
        setIsPrintVisitBadge,
        isPrintVisitBadge,
        showMessageErrorUniqueVisit,
        canceledVisits,
        setVisitDataByHostFields,
        getVisitDataByHostFields,
        getVisitByQRCode,
        navigateToHome,
        defaultCountryCode,
      }}
    >
      {children}
    </VisitContext.Provider>
  );
};

export function useVisit(): VisitContextData {
  const context = useContext(VisitContext);

  if (!context) {
    throw new Error('useVisit must be used within an VisitProvider');
  }

  return context;
}
